// @flow
import { fetchItemFacetsIfNeeded, getItemFacets } from '../../../redux/modules/itemFacets';
import { fetchItemsIfNeeded, getItem } from '../../../redux/modules/item';
import { fetchSearchFacetsIfNeeded, getRawSearchFacets } from '../../../redux/modules/searchFacets';
import { fetchSimilarItemsV3IfNeeded, getSimilarItemIds } from '../../../redux/modules/similarItemsV3';
import { getPromotedSearchResults, searchPromoted } from '../../../redux/modules/searchPromoted';
import { getSearchedItemIds, getSearchUiState } from '../../../redux/modules/search';
import { isArchivedSearch, parseQueryParams } from '../../../utils/queryParams';
import { buildSearchQuery } from '../../../utils/searchQuery';
import type { DataLoaderParams } from '../../../types/DataLoaderParams';
import { DEFAULT_SORT_ORDER } from '../../../utils/searchUtils';
import { fetchCatalogAdIfNeeded } from '../../../redux/modules/catalogAd';
import { fetchCategoriesIfNeeded } from '../../../redux/modules/category';
import { fetchItemBiddingIfNeeded } from '../../../redux/modules/biddingInfo';
import { getCategoryIdsFromPathname } from '../../../utils/urls';
import { getFacetIds } from '../../../utils/ItemPageUtils';
import { getSaveSearchParams } from '../../../redux/modules/saveSearch';
import { getSearchPageSize } from '../../../redux/modules/pagination';
import { isUserLoggedIn } from '../../../redux/modules/user';
import { recordAnalyticsForCategoryLandingPageView } from '../../../redux/modules/analytics';
import { searchCategory } from '../../../redux/modules/landingPage';
import { submitIsSavedSearchCheck } from '../../../redux/modules/saveSearchCheck';

const DEFAULT_PRICE_GUIDE_SORT = '-saleStart';
export const DATA_LOADING_ID = 'category-results-plus-promoted';

export default ({ location, match, store }: DataLoaderParams) => {
    const { params } = match;
    const { dispatch, getState } = store;
    const queryParams = parseQueryParams(location.search);
    const { pathname } = location;
    const archivedSearch = isArchivedSearch(queryParams, pathname);
    const pageName = pathname.includes('price-guide') ? 'price-guide' : 'category';

    return Promise.all([
        dispatch(fetchSearchFacetsIfNeeded(archivedSearch)),
        dispatch(fetchCatalogAdIfNeeded({ categoryIds: getCategoryIdsFromPathname(pathname), pageName })),
    ]).then(() => {
        const currentState = getState();
        const defaultSortOrder = archivedSearch ? DEFAULT_PRICE_GUIDE_SORT : DEFAULT_SORT_ORDER;
        const pageSize = getSearchPageSize(currentState);
        const rawFacets = getRawSearchFacets(currentState, archivedSearch);
        const searchQuery = buildSearchQuery({
            defaultSortOrder,
            pathname,
            queryParams,
            rawFacets,
            searchPageSize: pageSize,
        });

        const userLoggedIn = isUserLoggedIn(currentState);
        let landingPageIds = []; // add more id's here
        if (params.categoryId2) {
            landingPageIds = [Number(params.categoryId), Number(params.categoryId2)];
        } else if (params.categoryId) {
            landingPageIds = [Number(params.categoryId)];
        }

        const pageType = pathname.includes('/price-guide/') ? 'price guide' : 'category';
        if (userLoggedIn) {
            const ssParams = getSaveSearchParams(queryParams, pathname);
            dispatch(submitIsSavedSearchCheck(ssParams));
        }

        return Promise.all([
            dispatch(searchCategory(searchQuery, landingPageIds)),
            dispatch(searchPromoted({ ids: landingPageIds, pageName, searchQuery })),
            dispatch(fetchCategoriesIfNeeded(landingPageIds)),
        ]).then(() => {
            const searchState = getState();
            const itemIds = getSearchedItemIds(searchState);
            const promotedResults = getPromotedSearchResults(searchState);
            // make an array of requests to issue, dispatching analytics after they all complete
            // i have no idea why we care about `params.deleted` but apparently if it's
            // there we need to also fetch the item with its id
            if (itemIds?.length) {
                let itemPromises = [dispatch(fetchItemsIfNeeded([...itemIds, ...promotedResults], DATA_LOADING_ID))];
                if (params?.deleted && !isNaN(params.deleted)) {
                    itemPromises.push(dispatch(fetchItemsIfNeeded([params.deleted])));
                }
                return Promise.all(itemPromises).then(() => {
                    return dispatch(recordAnalyticsForCategoryLandingPageView(pageType));
                });
            } else {
                const { soldItemIds = [] } = getSearchUiState(searchState);
                const promises = [
                    dispatch(fetchItemsIfNeeded(soldItemIds, DATA_LOADING_ID)),
                    dispatch(fetchItemFacetsIfNeeded([soldItemIds[0]])),
                ];

                soldItemIds.forEach(id => promises.push(dispatch(fetchItemBiddingIfNeeded(id))));
                return Promise.all(promises).then(() => {
                    const state = getState();
                    const { itemId, sellerId, title } = getItem(state, soldItemIds[0]);
                    const facets = getItemFacets(state, itemId);
                    const categoryIds = getFacetIds(facets);
                    return Promise.all([
                        dispatch(
                            fetchSimilarItemsV3IfNeeded({
                                categoryIds,
                                itemId,
                                sellerId,
                                title,
                            }),
                        ),
                        dispatch(recordAnalyticsForCategoryLandingPageView(pageType)),
                    ]).then(() => {
                        const state = getState();
                        const similarItemIds = getSimilarItemIds(state, itemId);
                        return dispatch(fetchItemsIfNeeded(similarItemIds));
                    });
                });
            }
        });
    });
};
